import { useState } from "react";
import Flag from "@/components/Flag";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NATIONS, NATION_META, SETUP_FIELDS } from "@/lib/nations";
import { NationBadge, ScoreGauge } from "@/components/Compliance";
import UploadModal from "@/components/UploadModal";
import { toast } from "sonner";
import { ShieldCheck, Check, ArrowRight, Building2, Sparkles } from "lucide-react";

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [nations, setNations] = useState(user?.nations?.length ? user.nations : []);
  const [setup, setSetup] = useState(user?.nation_setup || {});
  const [prop, setProp] = useState({ name: "", address: "", postcode: "", nation: "", property_type: "single" });
  const [createdProp, setCreatedProp] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [score, setScore] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggleNation = (n) => setNations((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));

  const saveNations = async () => {
    if (!nations.length) return toast.error("Choose at least one nation");
    setBusy(true);
    try { await api.post("/onboarding/nations", { nations }); setProp((p) => ({ ...p, nation: nations[0] })); setStep(3); }
    finally { setBusy(false); }
  };

  const saveSetup = async () => {
    setBusy(true);
    try { await api.post("/onboarding/setup", { nation_setup: setup }); setStep(4); }
    finally { setBusy(false); }
  };

  const createProperty = async () => {
    if (!prop.name || !prop.address || !prop.nation) return toast.error("Fill in the property details");
    setBusy(true);
    try {
      const { data } = await api.post("/properties", prop);
      setCreatedProp(data);
      toast.success("Property added");
      setStep(5);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const loadScore = async () => {
    if (!createdProp) return;
    const { data } = await api.get(`/properties/${createdProp.id}`);
    setScore(data);
  };

  const finish = async () => {
    await api.post("/onboarding/complete");
    await refresh();
    nav("/dashboard");
  };

  const setField = (nation, key, val) => setSetup((p) => ({ ...p, [nation]: { ...(p[nation] || {}), [key]: val } }));

  const Progress = () => (
    <div className="flex items-center gap-2 mb-8">
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-emerald-brand" : "bg-border"}`} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 flex items-center px-4 sm:px-6 border-b border-border bg-card">
        <div className="flex items-center gap-2 font-heading font-extrabold text-lg">
          <span className="grid place-items-center h-8 w-8 rounded-lg bg-emerald-brand text-white"><ShieldCheck size={18} /></span> GetCerty
        </div>
      </header>
      <div className="max-w-xl mx-auto px-4 py-8 sm:py-12 pb-40">
        <Progress />

        {step === 1 && (
          <div className="animate-fade-up text-center" data-testid="onboard-step-1">
            <div className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-emerald-50 text-emerald-brand mb-4"><Sparkles size={30} /></div>
            <h1 className="font-heading text-2xl font-bold">Welcome to GetCerty, {user?.name?.split(" ")[0] || "landlord"} 👋</h1>
            <p className="mt-2 text-muted-foreground">Let's personalise GetCerty to the nations where you let property. It takes about 2 minutes.</p>
            <button data-testid="onboard-begin" onClick={() => setStep(2)} className="mt-6 w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors">Get started <ArrowRight size={18} /></button>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up" data-testid="onboard-step-2">
            <h1 className="font-heading text-2xl font-bold">Where do you let property?</h1>
            <p className="mt-1 text-muted-foreground">Pick all that apply — each property is tied to one nation.</p>
            <div className="mt-6 space-y-3">
              {NATIONS.map((n) => {
                const sel = nations.includes(n);
                return (
                  <button key={n} data-testid={`nation-${n.toLowerCase()}`} onClick={() => toggleNation(n)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${sel ? "border-emerald-brand bg-emerald-50/60" : "border-border hover:border-emerald-brand/50"}`}>
                    <Flag nation={n} className="h-7 w-10" />
                    <div className="flex-1">
                      <div className="font-semibold">{n}</div>
                      <div className="text-sm text-muted-foreground">{NATION_META[n].registration}</div>
                    </div>
                    <span className={`h-6 w-6 grid place-items-center rounded-full border-2 ${sel ? "bg-emerald-brand border-emerald-brand text-white" : "border-border"}`}>{sel && <Check size={14} />}</span>
                  </button>
                );
              })}
            </div>
            <button data-testid="nations-next" disabled={busy} onClick={saveNations} className="mt-6 w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors disabled:opacity-60">Continue</button>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up" data-testid="onboard-step-3">
            <h1 className="font-heading text-2xl font-bold">Nation-specific setup</h1>
            <p className="mt-1 text-muted-foreground">We'll track your own registration and renewal dates.</p>
            <div className="mt-6 space-y-6">
              {nations.map((n) => (
                <div key={n} className="bg-card border border-border rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4"><NationBadge nation={n} /><span className="text-sm text-muted-foreground">{NATION_META[n].registration}</span></div>
                  <div className="space-y-3">
                    {SETUP_FIELDS[n].map((f) => (
                      <label key={f.key} className="block">
                        <span className="text-xs font-medium text-muted-foreground">{f.label}</span>
                        {f.type === "select" ? (
                          <select data-testid={`setup-${n}-${f.key}`} value={setup[n]?.[f.key] || ""} onChange={(e) => setField(n, f.key, e.target.value)}
                            className="mt-1 w-full min-h-[44px] px-3 rounded-lg border border-input bg-card">
                            <option value="">Select…</option>
                            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input data-testid={`setup-${n}-${f.key}`} type={f.type} placeholder={f.placeholder} value={setup[n]?.[f.key] || ""} onChange={(e) => setField(n, f.key, e.target.value)}
                            className="mt-1 w-full min-h-[44px] px-3 rounded-lg border border-input bg-card" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="setup-next" disabled={busy} onClick={saveSetup} className="mt-6 w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors disabled:opacity-60">Continue</button>
          </div>
        )}

        {step === 4 && (
          <div className="animate-fade-up" data-testid="onboard-step-4">
            <h1 className="font-heading text-2xl font-bold">Add your first property</h1>
            <p className="mt-1 text-muted-foreground">We'll build its compliance checklist from your nation's rules.</p>
            <div className="mt-6 space-y-3">
              <input data-testid="prop-name" value={prop.name} onChange={(e) => setProp({ ...prop, name: e.target.value })} placeholder="Property name / nickname" className="w-full min-h-[48px] px-4 rounded-xl border border-input bg-card" />
              <input data-testid="prop-address" value={prop.address} onChange={(e) => setProp({ ...prop, address: e.target.value })} placeholder="Address" className="w-full min-h-[48px] px-4 rounded-xl border border-input bg-card" />
              <input data-testid="prop-postcode" value={prop.postcode} onChange={(e) => setProp({ ...prop, postcode: e.target.value })} placeholder="Postcode" className="w-full min-h-[48px] px-4 rounded-xl border border-input bg-card" />
              <label className="block"><span className="text-xs font-medium text-muted-foreground">Nation</span>
                <select data-testid="prop-nation" value={prop.nation} onChange={(e) => setProp({ ...prop, nation: e.target.value })} className="mt-1 w-full min-h-[48px] px-4 rounded-xl border border-input bg-card">
                  {nations.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <div>
                <span className="text-xs font-medium text-muted-foreground">Property type</span>
                <div className="mt-1 grid grid-cols-2 gap-3">
                  {[["single", "Single let"], ["hmo", "HMO"]].map(([v, l]) => (
                    <button key={v} data-testid={`prop-type-${v}`} onClick={() => setProp({ ...prop, property_type: v })}
                      className={`min-h-[48px] rounded-xl border-2 font-medium transition-colors ${prop.property_type === v ? "border-emerald-brand bg-emerald-50/60" : "border-border"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            <button data-testid="create-prop" disabled={busy} onClick={createProperty} className="mt-6 w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors disabled:opacity-60">Add property</button>
          </div>
        )}

        {step === 5 && createdProp && (
          <div className="animate-fade-up text-center" data-testid="onboard-step-5">
            <h1 className="font-heading text-2xl font-bold">Upload your first certificate</h1>
            <p className="mt-1 text-muted-foreground">Then see your compliance score instantly.</p>
            <div className="mt-6 bg-card border border-border rounded-2xl p-6 flex items-center gap-4 text-left">
              <Building2 className="text-emerald-brand" />
              <div className="flex-1"><div className="font-semibold">{createdProp.name}</div><div className="text-sm text-muted-foreground">{createdProp.address}</div></div>
              <NationBadge nation={createdProp.nation} />
            </div>
            {score ? (
              <div className="mt-6 flex flex-col items-center gap-3">
                <ScoreGauge score={score.score} />
                <p className="text-sm text-muted-foreground">{score.counts.green} compliant · {score.counts.amber} due soon · {score.counts.red} needs action</p>
              </div>
            ) : (
              <button data-testid="onboard-upload" onClick={() => setUploadOpen(true)} className="mt-6 w-full min-h-[48px] rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold flex items-center justify-center gap-2 transition-colors"><Sparkles size={18} /> Upload a certificate</button>
            )}
            <button data-testid="onboard-finish" onClick={finish} className="mt-3 w-full min-h-[48px] rounded-xl border border-border font-medium hover:bg-secondary transition-colors">{score ? "Go to dashboard" : "Skip for now — go to dashboard"}</button>
            <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} property={createdProp} onDone={loadScore} />
          </div>
        )}
      </div>
    </div>
  );
}
